<template>
  <article :class="['expansion-panel', {'isExpanded': referredIsExpanded }]">
    <header>
      <component :is="props.headingTag" v-clean-html="props.heading" class="h5" />
    </header>
    <div>
      <div ref="expandableContent" class="content">
        <slot></slot>
      </div>
    </div>
    <footer v-if="hasExpandableContent()" :class="['panel-footer', `text-${props.actionBackgroundColor}`]">
      <button @click="referredIsExpanded = !referredIsExpanded">
        <span class="text-s text-coral">{{ referredIsExpanded ? 'weniger lesen' : 'mehr lesen' }}</span>
        <Icon color="coral" name="chevron-down-line" />
      </button>
    </footer>
  </article>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Icon from '../Icon'
import { ColorTypes } from '../../../constants/enums'

const props = defineProps({
  heading: {
    type: String,
    default: '',
  },
  headingTag: {
    type: String,
    default: 'div',
    validator: (value) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', ''].includes(value),
  },
  previewHeightByLineNumber: {
    type: Number,
    default: 4,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  actionBackgroundColor: {
    type: String,
    default: 'white',
    validator: (value) => ColorTypes.includes(value),
  },
})

const referredIsExpanded = ref(props.isExpanded)
const previewHeight = ref('')
const expandedHeight = ref('')
const expandableContent = ref(null)
const previewHeightValue = ref (null)

const contentHeight = () => {
  if(!expandableContent.value){
    return null
  }

  expandedHeight.value = `${expandableContent.value.clientHeight}px`
  return expandedHeight.value
}

const previewHeightDefault = () => {
  const getPreviewHeight = parseFloat(window.getComputedStyle?.(document.querySelector('.expansion-panel > div .content'))
    .getPropertyValue?.('line-height')) * props.previewHeightByLineNumber

  previewHeight.value = `${getPreviewHeight}px`
  return previewHeight.value
}

const hasExpandableContent = () => {
  if(!previewHeight?.value || !expandedHeight?.value){
    return null
  }

  previewHeightValue.value = parseFloat(previewHeight.value)
    + parseFloat(window.getComputedStyle?.(document.querySelector('.expansion-panel > div .content p'))
      .getPropertyValue?.('margin-block')) * 2

  const contentHeightValue = parseFloat(contentHeight())

  return previewHeightValue.value < contentHeightValue
}

onMounted(() => {
  contentHeight()
  previewHeightDefault()
})
</script>

<style scoped lang="scss">
@use '../../../styles' as index;

.expansion-panel {
  padding: 1rem;
  max-width: var(--maxContainerWidth);
  margin-inline: auto;
  width: 100%;

  header {
    padding-bottom: 1rem;
  }

  & > div {
    max-height: v-bind(previewHeight);
    overflow: hidden;
    transition: max-height var(--time2) ease-in;

    .content {
      line-height: 1.5rem;
    }
  }

  .panel-footer {
    background: var(--current);
    background: linear-gradient(0deg, var(--current) 0%, var(--current) 81.25%, var(--transparent) 100%);
    padding: 1rem 0 .5rem;
    margin-top: -.5rem;
    position: relative;

    button {
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: flex-end;
      gap: 1rem;
    }

    span {
      font-weight: 600;  // 700 gibt es nur .. ist als platzhalter drin, bis hier ne entscheidung kommt oder einfach ne variable schrift genommen wird
      text-decoration: underline;
    }

    .icon {
      transition: rotate var(--time2) ease-out;
    }
  }

  &.isExpanded {
    & > div {
      max-height: v-bind(expandedHeight);
    }

    .panel-footer {
      .icon {
        rotate: 180deg;
      }
    }
  }
}

@media screen and (max-width: index.$breakpoint--s) {
  .expansion-panel {
    width: 100%;
  }
}
</style>
